import { baseDirection, invertDirection, isPlayableOn } from './overtakeCards';
import {
    SIDES,
    cardOf,
    drawFromStock,
    otherSide,
    refillHand,
} from './overtakeDeck';
import {
    availableSpecs,
    canCounterCall,
    canDouble,
    canVeto,
    leadableCards,
    legalPlays,
} from './overtakeMatch';
import { dearestCard, potValue } from './overtakeScoring';

/**
 * How a match changes (SPEC-011 PR-11.1).
 *
 * The other half of `overtakeMatch.js`, split from it at the seam the repo
 * contract's line limit forced and the coding guidelines already name —
 * mechanism against policy. That file answers what is TRUE of a match: whose
 * turn it is, which figures are left, which cards could legally go down. This
 * one is every way a match MOVES, and it is the only module in the tree that
 * returns a match different from the one it was given.
 *
 * Every move is total. An illegal one answers its own argument unchanged rather
 * than throwing, because the screen renders from this state and a refused move
 * still has to leave something on the glass — a second tap on a card already
 * down, or an opponent's timer landing in the same frame as a pass, are both
 * reachable by construction and neither may advance a round twice.
 */

function spend(match, side, power) {
    return {
        ...match,
        powers: {
            ...match.powers,
            [side]: { ...match.powers[side], [power]: false },
        },
    };
}

function withoutCard(hand, id) {
    const at = hand.indexOf(id);

    return at === -1 ? hand : [...hand.slice(0, at), ...hand.slice(at + 1)];
}

/**
 * Open a round: a card down and a figure named, in one move.
 *
 * One move rather than two, because they are one decision. A reader who could
 * name a figure and then choose a card would be choosing against a board that
 * had already told the opponent what was coming; a reader who could put a card
 * down and then think about the figure would be doing the same thing more
 * slowly. The screen collects both and commits them together.
 */
export function leadRound(match, deck, { carId, spec, doubled = false } = {}) {
    if (match?.status !== 'leading' || !leadableCards(match, deck, spec).includes(carId)) {
        return match;
    }

    const wants = doubled && canDouble(match);
    const led = {
        ...(wants ? spend(match, match.lead, 'double') : match),
        status: 'answering',
        hands: { ...match.hands, [match.lead]: withoutCard(match.hands[match.lead], carId) },
        pile: [{ id: carId, by: match.lead }],
        spec,
        direction: baseDirection(spec),
        doubled: wants,
        turn: otherSide(match.lead),
    };

    // Named, and therefore spent. A veto un-names it again (D-8), which is the
    // only path back out of this list.
    return { ...led, spent: [...match.spent, spec] };
}

/**
 * Refuse the figure and hand the naming back, without spending it.
 *
 * The leader's card stays exactly where it is. Only the figure is refused, and
 * it returns to the available list — which is what makes a veto a piece of
 * information rather than a way to delete the figure an opponent is strong on
 * (SPEC-011 D-8). The leader now knows something about a hand they could not
 * see, and paid nothing for knowing it except the figure they wanted.
 */
export function veto(match) {
    if (!canVeto(match)) {
        return match;
    }

    return {
        ...spend(match, match.turn, 'veto'),
        status: 'renaming',
        spec: null,
        direction: null,
        spent: match.spent.filter((spec) => spec !== match.spec),
        // The refused figure is remembered for one move so the screen can say
        // which one was turned down. It is cleared as the next round opens.
        vetoed: match.spec,
        turn: match.lead,
    };
}

/** Name a different figure for the card already on the table. */
export function renameSpec(match, deck, spec) {
    if (match?.status !== 'renaming') {
        return match;
    }

    const led = cardOf(deck, match.pile[0]?.id);

    if (!availableSpecs(match).includes(spec) || spec === match.vetoed || !isPlayableOn(led, spec)) {
        return match;
    }

    return {
        ...match,
        status: 'answering',
        spec,
        direction: baseDirection(spec),
        spent: [...match.spent, spec],
        turn: otherSide(match.lead),
    };
}

/**
 * Invert the figure, once per match, before answering it.
 *
 * The caller is bound by the inversion too (SPEC-011 D-7) — they still have to
 * beat the card on the table, now from the other end. That is the whole cost of
 * the power and it is what stops it being a free "you lose this round": a
 * counter-call on a figure you cannot then beat has spent your one inversion to
 * hand the leader a pot.
 */
export function counterCall(match) {
    if (!canCounterCall(match)) {
        return match;
    }

    return {
        ...spend(match, match.turn, 'counter'),
        direction: invertDirection(match.direction),
        countered: true,
    };
}

/** Climb over the card on the table. */
export function playCard(match, deck, carId) {
    if (!legalPlays(match, deck).includes(carId)) {
        return match;
    }

    return {
        ...match,
        hands: { ...match.hands, [match.turn]: withoutCard(match.hands[match.turn], carId) },
        pile: [...match.pile, { id: carId, by: match.turn }],
        turn: otherSide(match.turn),
    };
}

/**
 * Give up the round. The last card played takes everything on the table.
 *
 * A pass is always legal, in `answering` and in `renaming` alike — a leader who
 * has been vetoed and holds nothing playable on any remaining figure has to be
 * able to end the round, and a game that could deadlock because a rule was
 * written for the common case is a game that ends in a stuck screen.
 *
 * The forfeit is the one branch here that costs a card rather than a pot: a
 * leader who doubled and then lost surrenders their dearest remaining card into
 * the winner's pot (SPEC-011 D-10). It is taken from the HAND rather than from
 * the pot, so it is a card the reader was still going to play.
 */
export function pass(match, deck) {
    if (match?.status !== 'answering' && match?.status !== 'renaming') {
        return match;
    }

    const passer = match.turn;
    const top = match.pile[match.pile.length - 1];
    // Normally the top card belongs to the other side, because the turn flips
    // after every play and nobody is ever asked to answer themselves. The
    // exception is a leader who was vetoed and cannot name a second figure: they
    // are on their own card, and giving up there hands the round to the opponent
    // rather than awarding the leader the pot they just surrendered.
    const winner = top && top.by !== passer ? top.by : otherSide(passer);
    const cards = match.pile.map((played) => played.id);
    const forfeitsCard = match.doubled && winner !== match.lead && passer === match.lead;
    const forfeit = forfeitsCard ? dearestCard(deck, match.hands[match.lead])?.id ?? null : null;
    const taken = forfeit ? [...cards, forfeit] : cards;

    return {
        ...match,
        status: 'roundOver',
        // Cleared HERE rather than in `advanceRound`, so a card is never in two
        // places at once. `lastRound.cards` is what the screen draws the result
        // from, and leaving the pile populated beside the pot that now holds the
        // same ids would make "every card is in exactly one place" false for the
        // whole of the round-over state — an invariant worth more than the
        // convenience of reading the table twice.
        pile: [],
        hands: forfeit
            ? { ...match.hands, [match.lead]: withoutCard(match.hands[match.lead], forfeit) }
            : match.hands,
        pots: { ...match.pots, [winner]: [...match.pots[winner], ...taken] },
        // The second half of a doubled pot, which has no card to be (D-10).
        bonuses: match.doubled
            ? { ...match.bonuses, [winner]: match.bonuses[winner] + potValue(deck, taken) }
            : match.bonuses,
        lastRound: {
            spec: match.spec,
            direction: match.direction,
            doubled: match.doubled,
            countered: Boolean(match.countered),
            cards,
            forfeit,
            value: potValue(deck, taken) * (match.doubled ? 2 : 1),
            winner,
            passer,
        },
    };
}

/** Who opens the next round, under the rule this match was started with. */
function nextLead(match) {
    if (match.leadRule === 'alternate') {
        return otherSide(match.lead);
    }

    return match.leadRule === 'loser' ? match.lastRound.passer : match.lastRound.winner;
}

/**
 * Draw, refill, and open the next round — or end the match.
 *
 * The passer draws first (SPEC-011 D-9) and the refill happens after, so a
 * player who passed into a nearly-empty stock does not draw the same card twice.
 *
 * The match ends when EITHER hand is empty rather than both. A player with cards
 * facing one without them does not have a game left to play — every round would
 * be a lead that the other side passes immediately — and awarding a run of
 * one-card pots to whoever happened to be dealt the longer hand would decide the
 * match on the deal.
 */
export function advanceRound(match, deck) {
    if (match?.status !== 'roundOver') {
        return match;
    }

    const lead = nextLead(match);
    const slipstream = drawFromStock(match.stock, 1);
    const drawnBy = match.lastRound.passer;
    const withDraw = {
        ...match.hands,
        [drawnBy]: [...match.hands[drawnBy], ...slipstream.drawn],
    };
    const refilled = SIDES.reduce((carry, side) => {
        const filled = refillHand(carry.hands[side], carry.stock);

        return { hands: { ...carry.hands, [side]: filled.hand }, stock: filled.stock };
    }, { hands: withDraw, stock: slipstream.stock });

    const spent = SIDES.some((side) => refilled.hands[side].length === 0);
    const opened = {
        ...match,
        hands: refilled.hands,
        stock: refilled.stock,
        pile: [],
        spec: null,
        direction: null,
        doubled: false,
        countered: false,
        vetoed: null,
        lead,
        turn: lead,
        round: match.round + 1,
    };

    // A leader holding nothing playable on any remaining figure has no move, and
    // that is an ending rather than a deadlock — the same ending as an empty
    // hand, reached for a rarer reason.
    const canOpen = availableSpecs(opened)
        .some((spec) => leadableCards(opened, deck, spec).length > 0);

    return spent || !canOpen
        ? { ...opened, status: 'over', turn: null }
        : { ...opened, status: 'leading' };
}

/**
 * Apply a move descriptor.
 *
 * The one place a move NAME becomes a move, and it exists because two callers
 * need exactly that: the opponent answers a descriptor rather than a match
 * (SPEC-011 D-13), and PR-11.4's invited match receives one over the wire. A
 * `switch` at each of those call sites would be two spellings of the rulebook,
 * free to disagree the first time a power is added.
 *
 * An unknown move answers the match unchanged, exactly as an illegal one does.
 * A descriptor is the least trustworthy thing in this module — it arrives from a
 * bot, from a tap, or from a document — so it is parsed here rather than
 * trusted, which is the boundary the coding guidelines name.
 */
export function applyMove(match, deck, move) {
    switch (move?.type) {
        case 'lead':
            return leadRound(match, deck, move);
        case 'rename':
            return renameSpec(match, deck, move.spec);
        case 'play':
            return playCard(match, deck, move.carId);
        case 'counter':
            return counterCall(match);
        case 'veto':
            return veto(match);
        case 'pass':
            return pass(match, deck);
        case 'advance':
            return advanceRound(match, deck);
        default:
            return match;
    }
}
